import React, { useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { UseFormRegister, UseFormWatch, UseFormSetValue, FieldErrors } from 'react-hook-form';
import { TotalsDisplaySection } from './TotalsDisplaySection';
import { blobURL } from '../../common/StringUtils';
import { LOCALES } from '../../common/constants';

const expressDe = `${blobURL}/express-logo-de.png`;
const expressEng = `${blobURL}/express-logo-eng.png`;

interface ExpressPaymentSectionProps {
  // Customer & selection
  selectedCustomer: any;
  selectedFreelancersForInvoice: any[];
  
  // Form
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  errors: FieldErrors;
  
  // Express payment state
  isExpressCheckboxChecked: boolean;
  isEditModeInvoice: boolean;
  
  // Express payment handlers
  handleExpressPaymentCheckbox: (checked: boolean) => void;
  handleExpressPaymentReadMore: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  isExpressPaymentAvailable: () => boolean;
  getExpressPaymentMessage: () => string;
  
  // Totals data
  isCalculateInvoiceLoading: boolean;
  calculatedSubAmount: any;
  currency: string;
  language: string;
  vat: number;
  vatAmount: number;
  isCalculationLoading: boolean;
  handleShowCalculationBreakdown: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  
  // Translation
  t: (key: string) => string;
}

const ExpressPaymentSection: React.FC<ExpressPaymentSectionProps> = ({
  selectedCustomer,
  selectedFreelancersForInvoice,
  register,
  watch,
  setValue,
  errors,
  isExpressCheckboxChecked,
  isEditModeInvoice,
  handleExpressPaymentCheckbox,
  handleExpressPaymentReadMore,
  isExpressPaymentAvailable,
  getExpressPaymentMessage,
  isCalculateInvoiceLoading,
  calculatedSubAmount,
  currency,
  language,
  vat,
  vatAmount,
  isCalculationLoading,
  handleShowCalculationBreakdown,
  t,
}) => {
  // Get form value for expressPayout
  const expressPayoutValue = watch('expressPayout');
  
  // Debug: Check if express payment is available
  const isAvailable = isExpressPaymentAvailable();
 
  // Automatically uncheck checkbox if amount exceeds 60,000 SEK limit
  useEffect(() => {
    const amountIncludingVat = calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat || 0;
    
    // If checkbox is checked and amount exceeds limit, uncheck it
    if (expressPayoutValue && amountIncludingVat > 60000) {
      setValue('expressPayout', false);
      handleExpressPaymentCheckbox(false);
    }
  }, [calculatedSubAmount, expressPayoutValue, setValue, handleExpressPaymentCheckbox]);
  
  // Only show if freelancers are selected
  if (!selectedFreelancersForInvoice || selectedFreelancersForInvoice.length === 0) {
    return null;
  }

  return (
    <div className="express-payments-section">
      <Row className="express-payment-wrapper">
        {/* Left side: Express Payment Content */}
        {selectedCustomer?.country === 'Sweden' ? (
          <Col xs={12} md={12} lg={12} xl={7} xxl={8} className="express-payments-content mb-3 mb-xl-0">
            <h6 className="text-dark mb-3 mt-0 read">
              {t('Do you want Express payment on the same day?')}
              
              <a
                className="cursor-pointer how_calculate_link font-weight-bold h6 ml-1"
                onClick={handleExpressPaymentReadMore}
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                {t('Read more')}
              </a>
            </h6>
            
            {/* Checkbox - Disabled when BankID not available */}
            <div className="d-flex align-items-center mb-3">
              <input
                type="checkbox"
                id="express-payments"
                {...register('expressPayout', {
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    handleExpressPaymentCheckbox(e.target.checked);
                  }
                })}
                className="custom-checkbox"
                disabled={isEditModeInvoice || !isExpressPaymentAvailable()}
                checked={expressPayoutValue || false}
              />
              <label htmlFor="express-payments" className="mb-0 ml-2">
                <img
                  src={language === LOCALES.SV_SE ? expressDe : expressEng}
                  alt="express-payment-logo"
                  className="express-logo"
                  style={{ width: '150px' }}
                  loading="lazy"
                />
              </label>
            </div>

            {/* Warning Message - Show when Express Payment is not available */}
            {!isExpressPaymentAvailable() && (
              <div className="mt-0 mb-3">
                <div 
                  className="alert alert-warning d-flex align-items-center" 
                  style={{ 
                    backgroundColor: '#fff3cd', 
                    border: '1px solid #ffeaa7', 
                    borderRadius: '8px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  <i 
                    className="fas fa-exclamation-triangle mr-2" 
                    style={{ color: '#856404', fontSize: '16px' }}
                  ></i>
                  <span style={{ color: '#856404' }}>
                    <strong>{t('Action Required:')}</strong> {getExpressPaymentMessage()}
                  </span>
                </div>
              </div>
            )}

            {errors.expressPayout && (
              <div className="error-message">{errors.expressPayout.message?.toString()}</div>
            )}
          </Col>
        ) : (
          <Col xs={12} md={12} lg={12} xl={7} xxl={8} className="express-payments-content mb-3 mb-xl-0"></Col>
        )}

        {/* Right side: Totals Section */}
        <TotalsDisplaySection
          isCalculateInvoiceLoading={isCalculateInvoiceLoading}
          calculatedSubAmount={calculatedSubAmount}
          currency={currency}
          language={language}
          vat={vat}
          vatAmount={vatAmount}
          isCalculationLoading={isCalculationLoading}
          handleShowCalculationBreakdown={handleShowCalculationBreakdown}
          t={t}
        />
      </Row>
    </div>
  );
};

export default ExpressPaymentSection;
